import { type ReactNode, useState } from 'react';
import type { Approacher } from '../types';
import { useUpdateTeamMember } from '../hooks/use-approacher-mutations';
import { joinPhone, splitPhone } from '../utils/phone-helpers';
import { ApproacherEditFields, type EditForm } from './approacher-edit-fields';
import { ApproacherEditActions } from './approacher-edit-actions';

const s = (v: string | undefined): string => v || '';

function init(a: Approacher): EditForm {
  const phone = splitPhone(s(a.phone));
  return {
    first_name: s(a.first_name),
    last_name: s(a.last_name),
    ig_username: a.ig_username,
    personal_ig_username: s(a.personal_ig_username),
    email: s(a.email),
    phone_prefix: phone.prefix,
    phone_number: phone.number,
    country: s(a.country),
    city: s(a.city),
    birthdate: a.birthdate ? a.birthdate.slice(0, 10) : '',
    role: a.role,
    manychat_subscriber_id: s(a.manychat_subscriber_id),
    usdc_wallet: s(a.usdc_wallet),
    is_active: a.is_active,
    member_color: s(a.member_color),
  };
}

type Payload = Partial<Approacher> & { phone?: string };

function buildPayload(f: EditForm, a: Approacher): Payload {
  const p: Payload = {
    first_name: f.first_name.trim(),
    last_name: f.last_name.trim(),
    ig_username: f.ig_username.trim(),
    personal_ig_username: f.personal_ig_username
      .replace(/^@/, '')
      .toLowerCase()
      .trim(),
    phone: joinPhone(f.phone_prefix, f.phone_number),
    country: f.country,
    city: f.city.trim(),
    birthdate: f.birthdate,
    role: f.role,
    manychat_subscriber_id: f.manychat_subscriber_id.trim(),
    usdc_wallet: f.usdc_wallet.trim(),
    is_active: f.is_active,
    member_color: f.member_color.trim(),
  };
  const e = f.email.trim();
  if (e && e !== s(a.email)) p.email = e;
  return p;
}

type Props = {
  approacher: Approacher;
  onSave: () => void;
  onCancel: () => void;
};

function useEditForm(approacher: Approacher, onSave: () => void) {
  const [f, setF] = useState<EditForm>(init(approacher));
  const update = useUpdateTeamMember();
  const set = (k: keyof EditForm, v: string | boolean): void =>
    setF({ ...f, [k]: v });
  const save = (): void =>
    update.mutate(
      { id: approacher.id, data: buildPayload(f, approacher) },
      { onSuccess: onSave }
    );
  return { f, set, save, pending: update.isPending };
}

const SHELL = 'flex max-h-[70vh] flex-col';
const SCROLL = 'flex-1 overflow-y-auto pb-base pr-1';
const ACTION_BAR =
  '-mx-md mt-auto shrink-0 border-t border-white/[0.06] bg-surface/85 px-md py-sm backdrop-blur-md sm:-mx-lg sm:px-lg';

export function ApproacherEditForm(p: Props): ReactNode {
  const { f, set, save, pending } = useEditForm(p.approacher, p.onSave);
  return (
    <div className={SHELL}>
      <div className={SCROLL}>
        <ApproacherEditFields
          f={f}
          set={set}
          approacher={p.approacher}
          onUpdated={p.onSave}
        />
      </div>
      <div className={ACTION_BAR}>
        <ApproacherEditActions
          onCancel={p.onCancel}
          onSave={save}
          disabled={pending}
        />
      </div>
    </div>
  );
}
